/**
 * Chamber map positions. The doctor (or staff) pastes a Google Maps link or
 * types coordinates; we keep the latitude and longitude only. Same rules as
 * the server (`GeoPoint::new`): in range, and never `0, 0`.
 */
export interface LatLng {
  lat: number;
  lng: number;
}

export type LocationParse = { kind: "empty" } | { kind: "ok"; point: LatLng } | { kind: "short-link" } | { kind: "invalid" };

const NUM = "(-?\\d{1,3}(?:\\.\\d+)?)";
const PAIR = new RegExp(`^\\s*${NUM}\\s*[,\\s]\\s*${NUM}\\s*$`);

function point(lat: number, lng: number): LatLng | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  // Six decimals, like the server keeps.
  const p = { lat: Math.round(lat * 1e6) / 1e6, lng: Math.round(lng * 1e6) / 1e6 };
  return p.lat === 0 && p.lng === 0 ? null : p;
}

function pair(text: string): LatLng | null {
  const m = PAIR.exec(text);
  return m ? point(Number(m[1]), Number(m[2])) : null;
}

/**
 * Reads a position from typed coordinates (`23.7808, 90.4073`) or a Google
 * Maps address. A place link's pin (`!3d…!4d…`) wins over the map centre
 * (`@lat,lng`), which can sit a little off the chamber.
 */
export function parseLocation(raw: string): LocationParse {
  const text = raw.trim();
  if (text === "") return { kind: "empty" };
  const direct = pair(text);
  if (direct) return { kind: "ok", point: direct };
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return { kind: "invalid" };
  }
  const host = url.hostname.toLowerCase();
  if (host === "maps.app.goo.gl" || host === "goo.gl") return { kind: "short-link" };
  const decoded = decodeURIComponent(url.href);
  const pin = new RegExp(`!3d${NUM}!4d${NUM}`).exec(decoded);
  if (pin) {
    const p = point(Number(pin[1]), Number(pin[2]));
    if (p) return { kind: "ok", point: p };
  }
  for (const key of ["q", "query", "ll", "destination", "center", "daddr"]) {
    const v = url.searchParams.get(key);
    const p = v === null ? null : pair(v);
    if (p) return { kind: "ok", point: p };
  }
  const centre = new RegExp(`@${NUM},${NUM}`).exec(decoded);
  if (centre) {
    const p = point(Number(centre[1]), Number(centre[2]));
    if (p) return { kind: "ok", point: p };
  }
  return { kind: "invalid" };
}

/** How a saved position reads in the box. */
export function formatLocation(p: LatLng | null | undefined): string {
  return p ? `${p.lat}, ${p.lng}` : "";
}

/** Google Maps at the position, to check it. */
export function mapsPinUrl(p: LatLng): string {
  return `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`;
}

/** Google Maps searching for the chamber, to find it and copy its link. */
export function mapsSearchUrl(name: string, address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([name, address].filter((s) => s.trim() !== "").join(", "))}`;
}
